/**
 * Working Hours Dual-Write Test
 *
 * Tests that working hours operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * WH1: Create working hours (hourly)
 * WH2: Update working hours
 * WH3: Delete working hours
 */

import {
  saveWorkingHours,
  deleteWorkingHours,
} from '../global/Firebase/workingHours';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, usersDictAtom, dbAtom, organizationIdAtom } from '../global/AtomsProvider';
import { getDailyStatID } from './timezoneFunctions';

// Test variation definitions
export const WORKING_HOURS_TEST_VARIATIONS = [
  { id: 'WH1', category: 'create', name: 'Create working hours (hourly)' },
  { id: 'WH2', category: 'update', name: 'Update working hours' },
  { id: 'WH3', category: 'delete', name: 'Delete working hours' },
];

// Get test categories for UI grouping
export function getWorkingHoursTestCategories() {
  return [
    { id: 'create', name: 'Create Working Hours', variations: ['WH1'] },
    { id: 'update', name: 'Update Working Hours', variations: ['WH2'] },
    { id: 'delete', name: 'Delete Working Hours', variations: ['WH3'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getWorkingHoursTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const usersDict = jotaiStore.get(usersDictAtom);
  const db = jotaiStore.get(dbAtom);
  const organizationId = jotaiStore.get(organizationIdAtom);

  if (!organization || !db) {
    return null;
  }

  // Get first store
  const storeEntries = Object.entries(storesDict || {});
  if (storeEntries.length < 1) {
    return null;
  }

  const store = { id: storeEntries[0][0], ...storeEntries[0][1] };

  // Get first employee (user)
  const userEntries = Object.entries(usersDict || {});
  if (userEntries.length < 1) {
    return null;
  }

  const employee = { id: userEntries[0][0], ...userEntries[0][1] };

  return {
    organization,
    store,
    employee,
    db,
    organizationId,
  };
}

/**
 * Compare working hours via backend API
 */
async function compareWorkingHoursViaApi(workingHoursId, workingHoursData, log) {
  try {
    const response = await api.get(`/working-hours/compare/${workingHoursId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        workingHoursId,
        foundInSupabase: false,
        matches: false,
        differences: [{
          field: 'working_hours',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare key fields
    const fieldsToCompare = [
      { fs: 'amount', sb: 'amount' },
      { fs: 'salaryType', sb: 'salary_type' },
    ];

    for (const { fs, sb } of fieldsToCompare) {
      const fsVal = normalizeValue(workingHoursData?.[fs]);
      const sbVal = normalizeValue(supabaseData?.[sb]);

      if (!valuesMatch(fsVal, sbVal)) {
        differences.push({
          field: sb,
          supabaseValue: sbVal,
          firestoreValue: fsVal,
          severity: 'critical',
        });
      }
    }

    return {
      workingHoursId,
      foundInSupabase: true,
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      warnings: differences.filter(d => d.severity === 'warning').map(d => d.field),
      supabaseData,
    };
  } catch (error) {
    log(`API comparison error: ${error.message}`);
    return {
      workingHoursId,
      foundInSupabase: false,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Check if working hours was deleted from Supabase
 */
async function checkWorkingHoursDeleted(workingHoursId, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await api.get(`/working-hours/compare/${workingHoursId}`);
      if (!response.data.exists) {
        return true;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      // 404 means deleted
      if (error.response?.status === 404) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Run a single working hours test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, store, employee, organizationId } = testData;

  let createdRef = null;
  let createdWorkingHoursData = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'WH1': {
        // Create working hours (hourly)
        const workingHoursDate = new Date();
        const dailyStatID = getDailyStatID(null, workingHoursDate) + '-' + store.id;

        const workingHoursData = {
          employeeID: employee.id,
          salaryType: 'hour',
          startHour: { value: 9, text: '09:00' },
          endHour: { value: 18, text: '18:00' },
          amount: 100, // hourly rate * hours
          date: workingHoursDate,
          dailyStatID: dailyStatID,
          store: store.id,
          organizationId,
        };

        log(`Creating working hours for employee: "${employee.name || employee.id}"`);
        createdRef = await saveWorkingHours(null, workingHoursData);
        createdWorkingHoursData = { ...workingHoursData, id: createdRef.id, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareWorkingHoursViaApi(createdRef.id, workingHoursData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test working hours...');
        await deleteWorkingHours(createdWorkingHoursData);
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'WH2': {
        // Update working hours test
        const workingHoursDate = new Date();
        const dailyStatID = getDailyStatID(null, workingHoursDate) + '-' + store.id;

        const workingHoursData = {
          employeeID: employee.id,
          salaryType: 'hour',
          startHour: { value: 9, text: '09:00' },
          endHour: { value: 17, text: '17:00' },
          amount: 80, // initial amount
          date: workingHoursDate,
          dailyStatID: dailyStatID,
          store: store.id,
          organizationId,
        };

        log('Creating working hours to update...');
        createdRef = await saveWorkingHours(null, workingHoursData);
        createdWorkingHoursData = { ...workingHoursData, id: createdRef.id, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareWorkingHoursViaApi(createdRef.id, workingHoursData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Working hours was not created in Supabase';
          break;
        }

        // Now update
        const updatedData = {
          ...createdWorkingHoursData,
          endHour: { value: 18, text: '18:00' },
          amount: 100, // updated amount
        };

        log('Updating working hours...');
        await saveWorkingHours(createdWorkingHoursData, updatedData);

        log('Waiting 6000ms for update sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare updated values
        const updateCheck = await compareWorkingHoursViaApi(createdRef.id, updatedData, log);
        result.comparisonResult = updateCheck;
        result.passed = updateCheck.foundInSupabase && valuesMatch(updateCheck.supabaseData?.amount, 100);

        // Cleanup
        log('Cleaning up test working hours...');
        await deleteWorkingHours({ ...updatedData, id: createdRef.id, ref: createdRef });
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'WH3': {
        // Delete working hours test
        const workingHoursDate = new Date();
        const dailyStatID = getDailyStatID(null, workingHoursDate) + '-' + store.id;

        const workingHoursData = {
          employeeID: employee.id,
          salaryType: 'day',
          amount: 200, // daily rate
          date: workingHoursDate,
          dailyStatID: dailyStatID,
          store: store.id,
          organizationId,
        };

        log('Creating working hours to delete...');
        createdRef = await saveWorkingHours(null, workingHoursData);
        createdWorkingHoursData = { ...workingHoursData, id: createdRef.id, ref: createdRef };

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareWorkingHoursViaApi(createdRef.id, workingHoursData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Working hours was not created in Supabase';
          break;
        }

        // Now delete
        log('Deleting working hours...');
        await deleteWorkingHours(createdWorkingHoursData);

        log('Waiting 6000ms for delete sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Check if deleted
        const deleted = await checkWorkingHoursDeleted(createdRef.id);
        result.passed = deleted;
        result.comparisonResult = {
          workingHoursId: createdRef.id,
          foundInSupabase: !deleted,
          matches: deleted,
          differences: deleted ? [] : [{
            field: 'deleted',
            supabaseValue: 'still exists',
            firestoreValue: 'deleted',
            severity: 'critical',
          }],
        };

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdRef && createdWorkingHoursData) {
      try {
        await deleteWorkingHours(createdWorkingHoursData);
      } catch (cleanupError) {
        log(`Cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all working hours tests
 */
export async function runFullWorkingHoursTest(log = console.log, selectedVariations = null) {
  log('Starting working hours dual-write tests...');

  const testData = getWorkingHoursTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Need at least 1 store and 1 employee.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('WORKING HOURS DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`Store: ${testData.store.name || testData.store.id}`);
  log(`Employee: ${testData.employee.name || testData.employee.id}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || WORKING_HOURS_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = WORKING_HOURS_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = WORKING_HOURS_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  WORKING_HOURS_TEST_VARIATIONS,
  getWorkingHoursTestCategories,
  getWorkingHoursTestData,
  runFullWorkingHoursTest,
};
